import { useCallback, useEffect, useState } from "react";
import { Search, RefreshCw, ChevronDown, ChevronRight } from "lucide-react";
import { api, money, errorMessage } from "../api";
import { Printer } from "lucide-react";
import { PaperSelect, printThermalOrA4, kotThermal } from "../lib/printdoc";
import { DateInput } from "../components/DateInput";

export const CHANGE_LABEL = { increase: "Qty increased", reduce: "Qty reduced", cancel: "Cancelled", note: "Note changed" };

export function fmtDateTime(iso) {
  const d = new Date(iso);
  const p = new Intl.DateTimeFormat("en-GB", { timeZone: "Asia/Kolkata", day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit", hour12: true }).formatToParts(d);
  const g = (t) => p.find((x) => x.type === t)?.value;
  return `${g("day")}-${g("month")}-${g("year")} ${g("hour")}:${g("minute")} ${(g("dayPeriod") || "").toUpperCase()}`;
}

export function ModRow({ m }) {
  return (
    <tr data-testid={`kot-mod-${m.id}`}>
      <td><b>{m.item_name}</b><span>{m.kot_no} → {m.mod_kot_no}</span></td>
      <td><span className={`pill mod-${m.change}`} data-testid={`kot-mod-change-${m.id}`}>{CHANGE_LABEL[m.change]}</span></td>
      <td>{m.old_qty} → {m.new_qty}{m.new_notes !== m.old_notes && <span>“{m.new_notes || "—"}”</span>}</td>
      <td>{m.table_label}</td>
      <td>{m.reason || "—"}</td>
      <td>{m.by_name}<span>{(m.by_role || "").toUpperCase()}</span></td>
      <td>{fmtDateTime(m.at)}</td>
    </tr>
  );
}

export function ModTable({ rows, testid }) {
  return (
    <div className="table-wrap"><table data-testid={testid}>
      <thead><tr><th>Item / KOT</th><th>Change</th><th>Qty / note</th><th>Table</th><th>Reason</th><th>By</th><th>When (IST)</th></tr></thead>
      <tbody>
        {!rows.length && <tr><td colSpan={7}><div className="empty small">No modifications recorded.</div></td></tr>}
        {rows.map((m) => <ModRow key={m.id} m={m} />)}
      </tbody>
    </table></div>
  );
}

const istDay = (offset) => new Date(Date.now() + 5.5 * 3600000 + offset * 86400000).toISOString().slice(0, 10);
const STATUS_PILL = { open: "pending", billing: "pending", billed: "success", merged: "neutral" };

function OrderCard({ o }) {
  const [open, setOpen] = useState(false);
  const live = o.items.filter((i) => i.status !== "cancelled");
  return (
    <div className="panel" style={{ padding: 12, marginBottom: 10 }} data-testid={`kot-order-${o.tab_id}`}>
      <button className="text-button" style={{ width: "100%", display: "flex", gap: 10, alignItems: "center", textAlign: "left" }} onClick={() => setOpen(!open)} data-testid={`kot-order-toggle-${o.tab_id}`}>
        {open ? <ChevronDown size={15} /> : <ChevronRight size={15} />}
        <b>{o.table_label || "Takeaway"}</b>
        <span className={`status ${STATUS_PILL[o.status] || "neutral"}`} data-testid={`kot-order-status-${o.tab_id}`}>{o.status === "billed" ? "Billed" : o.status === "merged" ? "Merged" : "Open"}</span>
        <span className="muted-label">{o.waiter_name || "—"} · opened {fmtDateTime(o.opened_at)}{o.closed_at ? ` · closed ${fmtDateTime(o.closed_at)}` : ""}</span>
        <span style={{ flex: 1 }} />
        <span className="muted-label">{o.kots.length} KOTs · {o.modifications.length} changes · {live.length} items</span>
        {o.invoice && <b data-testid={`kot-order-invoice-${o.tab_id}`}>{o.invoice.invoice_no} · {money(o.invoice.grand_total)}{o.invoice.status === "cancelled" ? " (CANCELLED)" : ""}</b>}
      </button>
      {open && (
        <div style={{ marginTop: 10 }}>
          <div className="table-wrap"><table data-testid={`kot-order-kots-${o.tab_id}`}><thead><tr><th>KOT / round</th><th>Items</th><th>Kitchen status</th><th>Printed (IST)</th><th>By</th><th></th></tr></thead><tbody>
            {o.kots.map((k) => (
              <tr key={k.id}>
                <td><b>{k.kot_no}</b><span>{k.type === "modification" ? `Change to ${k.ref_kot_no}` : "Order round"}</span></td>
                <td>{k.items.map((i) => <div key={i.item_id || i.name} style={i.status === "cancelled" ? { textDecoration: "line-through", opacity: 0.6 } : undefined}>{i.qty} × {i.name}{i.notes ? ` (${i.notes})` : ""}{i.change ? ` — ${CHANGE_LABEL[i.change] || i.change}` : ""}</div>)}</td>
                <td><span className="pill neutral">{k.status}{k.closed_by_bill ? " · closed on bill" : ""}</span></td>
                <td>{fmtDateTime(k.printed_at)}</td>
                <td>{k.modified_by || k.waiter_name || "—"}</td>
                <td><button className="icon-button" title="Reprint KOT" onClick={() => printThermalOrA4(k.kot_no, kotThermal(k, (i) => i.qty, (i) => i.notes))} data-testid={`kot-reprint-${k.id}`}><Printer size={13} /></button></td>
              </tr>
            ))}
            {!o.kots.length && <tr><td colSpan={6}><div className="empty small">No KOT sent.</div></td></tr>}
          </tbody></table></div>
          {o.modifications.length > 0 && <ModTable rows={o.modifications} testid={`kot-order-mods-${o.tab_id}`} />}
        </div>
      )}
    </div>
  );
}

function OrdersTimeline({ branch }) {
  const [f, setF] = useState({ q: "", status: "", date_from: istDay(0), date_to: istDay(0) });
  const [data, setData] = useState({ orders: [] });
  const [err, setErr] = useState("");
  const load = useCallback(async () => {
    try { setData((await api.get(`/kot-history/orders`, { params: { branch, ...f } })).data); setErr(""); } catch (e) { setErr(errorMessage(e)); }
  }, [branch, f]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);
  const upd = (k, v) => setF((x) => ({ ...x, [k]: v }));
  return (
    <>
      <div className="filters kot-filters">
        <div className="search"><Search size={14} /><input placeholder="Table, waiter, KOT, invoice, dish" value={f.q} onChange={(e) => upd("q", e.target.value)} data-testid="kot-orders-search" /></div>
        <select value={f.status} onChange={(e) => upd("status", e.target.value)} data-testid="kot-orders-status"><option value="">All orders</option><option value="open">Open</option><option value="billed">Billed</option><option value="merged">Merged</option></select>
        <label>From<DateInput value={f.date_from} max={f.date_to} onChange={(e) => upd("date_from", e.target.value)} data-testid="kot-orders-from" /></label>
        <label>To<DateInput value={f.date_to} min={f.date_from} onChange={(e) => upd("date_to", e.target.value)} data-testid="kot-orders-to" /></label>
        <span className="filter-spacer" />
        <PaperSelect testid="kot-paper-width" />
        <span className="muted-label" data-testid="kot-orders-count">{data.orders.length} orders</span>
        <button className="icon-button" onClick={load} data-testid="kot-orders-refresh"><RefreshCw size={14} /></button>
      </div>
      {err && <div className="auth-alert error" style={{ margin: 16 }} data-testid="kot-orders-error">{err}</div>}
      <div style={{ padding: 12 }}>
        {data.orders.map((o) => <OrderCard key={o.tab_id} o={o} />)}
        {!data.orders.length && <div className="empty" data-testid="kot-orders-empty">No orders in this period.</div>}
      </div>
    </>
  );
}

export default function KotHistoryView({ branch }) {
  const [tab, setTab] = useState("orders");
  return (
    <div className="panel full-panel" data-testid="kot-history-page">
      <div className="filters">
        <button className={tab === "orders" ? "primary-button" : "secondary-button"} onClick={() => setTab("orders")} data-testid="kot-history-tab-orders">Orders &amp; KOT rounds</button>
        <button className={tab === "mods" ? "primary-button" : "secondary-button"} onClick={() => setTab("mods")} data-testid="kot-history-tab-mods">Modifications log</button>
      </div>
      {tab === "orders" ? <OrdersTimeline branch={branch} /> : <ModsLog branch={branch} />}
    </div>
  );
}

function ModsLog({ branch }) {
  const [rows, setRows] = useState([]);
  const [f, setF] = useState({ change: "", q: "", date_from: "", date_to: "" });
  const [err, setErr] = useState("");
  const load = useCallback(async () => {
    try {
      const qs = new URLSearchParams({ branch, ...f }).toString();
      setRows((await api.get(`/kot-history?${qs}`)).data); setErr("");
    } catch (e) { setErr(errorMessage(e)); }
  }, [branch, f]);
  useEffect(() => { load(); }, [load]);
  const upd = (k, v) => setF((x) => ({ ...x, [k]: v }));
  return (
    <div data-testid="kot-mods-log">
      <div className="filters kot-filters">
        <div className="search"><Search size={14} /><input placeholder="Item, table, KOT, staff, reason" value={f.q} onChange={(e) => upd("q", e.target.value)} data-testid="kot-history-search" /></div>
        <select value={f.change} onChange={(e) => upd("change", e.target.value)} data-testid="kot-history-change-filter">
          <option value="">All changes</option>
          {Object.entries(CHANGE_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        <label>From<DateInput lang="en-GB" value={f.date_from} onChange={(e) => upd("date_from", e.target.value)} data-testid="kot-history-from" /></label>
        <label>To<DateInput lang="en-GB" value={f.date_to} onChange={(e) => upd("date_to", e.target.value)} data-testid="kot-history-to" /></label>
        <span className="filter-spacer" />
        <span className="muted-label" data-testid="kot-history-count">{rows.length} entries</span>
        <button className="icon-button" onClick={load} data-testid="kot-history-refresh"><RefreshCw size={14} /></button>
      </div>
      {err && <div className="auth-alert error" style={{ margin: 16 }}>{err}</div>}
      <ModTable rows={rows} testid="kot-history-table" />
    </div>
  );
}
